import {
  Check,
  CheckCheck,
  FileText,
  FolderOpen,
  MessageCircle,
  Send,
  Smartphone,
  Zap,
} from "lucide-react";
import { useGetIdentity, useNotify, type Identifier } from "ra-core";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { MaterialPicker } from "../materials/MaterialPicker";
import type { Material } from "../materials/materials";
import { QuickRepliesPanel } from "../quickReplies/QuickRepliesPanel";
import { CHANNEL_LABEL, type ChatChannel } from "../social/social";
import {
  markSocialRead,
  sendSocial,
  sendSocialMaterial,
  useSocialMessages,
} from "../social/useSocial";
import { agentName, useAgents } from "../useAgents";
import {
  isWindowOpen,
  shortTime,
  windowLeft,
  type WaMessage,
} from "./whatsapp";
import {
  markThreadRead,
  sendWhatsapp,
  sendWhatsappMaterial,
  sendWhatsappWelcome,
  useWaMessages,
  useWelcomeEnabled,
  WindowClosedError,
} from "./useWhatsapp";

const StatusIcon = ({ status }: { status?: string | null }) => {
  if (status === "read")
    return <CheckCheck className="h-3.5 w-3.5 text-sky-600" />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5" />;
  if (status === "failed")
    return <span className="text-[10px] text-destructive">no enviado</span>;
  return <Check className="h-3.5 w-3.5" />;
};

const Bubble = ({
  message,
  senderName,
}: {
  message: WaMessage;
  senderName?: string;
}) => {
  const isOut = message.direction === "out";
  return (
    <div className={cn("flex", isOut ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "flex max-w-[80%] flex-col gap-1 rounded-2xl px-3 py-2 text-sm shadow-sm",
          isOut
            ? "rounded-br-sm bg-lime-200 text-neutral-900"
            : "rounded-bl-sm bg-white text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100",
        )}
      >
        {message.media_url && message.type === "image" ? (
          <a href={message.media_url} target="_blank" rel="noreferrer">
            <img
              src={message.media_url}
              alt="Foto"
              className="max-h-60 rounded-lg object-cover"
            />
          </a>
        ) : null}
        {message.media_url && message.type === "audio" ? (
          <audio controls src={message.media_url} className="max-w-full" />
        ) : null}
        {message.media_url &&
        (message.type === "video" ||
          (message.type === "template" &&
            message.media_type?.startsWith("video/"))) ? (
          <video
            controls
            preload="metadata"
            src={message.media_url}
            className="max-h-72 rounded-lg"
          />
        ) : null}
        {message.media_url &&
        !["image", "audio", "video", "template"].includes(message.type) ? (
          <a
            href={message.media_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            <FileText className="h-4 w-4" />
            {message.file_name ?? "Archivo"}
          </a>
        ) : null}
        {message.body ? (
          <span className="whitespace-pre-wrap break-words">
            {message.body}
          </span>
        ) : null}
        {!message.body && !message.media_url ? (
          <span className="italic text-neutral-500">
            ({message.type} no soportado)
          </span>
        ) : null}
        <span className="flex items-center justify-end gap-1 text-[11px] text-neutral-500">
          {isOut ? (
            message.origin === "phone" ? (
              <span className="inline-flex items-center gap-0.5">
                <Smartphone className="h-3 w-3" />
                móvil
              </span>
            ) : message.origin === "auto" ? (
              <span>automático</span>
            ) : senderName ? (
              <span>{senderName}</span>
            ) : null
          ) : null}
          {shortTime(message.created_at)}
          {isOut ? <StatusIcon status={message.status} /> : null}
        </span>
      </div>
    </div>
  );
};

/**
 * A conversation with a client on WhatsApp, Instagram or Messenger: bubbles,
 * photos, status ticks and a composer with quick replies and the materials
 * library. Outside WhatsApp's 24 h window it offers the approved welcome.
 */
export const WhatsappThread = ({
  channel = "whatsapp",
  contactId,
  threadId,
  phone,
  firstName,
  className,
}: {
  channel?: ChatChannel;
  /** The client (WhatsApp conversations are keyed by contact). */
  contactId?: Identifier | null;
  /** The Instagram or Messenger conversation. */
  threadId?: Identifier | null;
  phone?: string | null;
  firstName?: string | null;
  className?: string;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { findAgent } = useAgents();
  const isWhatsapp = channel === "whatsapp";
  const welcomeReady = useWelcomeEnabled();
  const whatsapp = useWaMessages(isWhatsapp ? contactId : null);
  const social = useSocialMessages(isWhatsapp ? null : threadId);
  const { messages, isPending, reload } = isWhatsapp ? whatsapp : social;
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [panel, setPanel] = useState<"replies" | "materials" | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const lastInbound = [...messages]
    .reverse()
    .find((message) => message.direction === "in")?.created_at;
  const windowOpen = isWindowOpen(lastInbound);
  const myFirstName =
    typeof identity?.fullName === "string"
      ? identity.fullName.split(" ")[0]
      : "";

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (!messages.some((message) => message.direction === "in")) return;
    if (isWhatsapp && contactId != null) markThreadRead(contactId);
    if (!isWhatsapp && threadId != null) markSocialRead(threadId);
  }, [isWhatsapp, contactId, threadId, messages]);

  const sendText = (body: string) =>
    isWhatsapp
      ? sendWhatsapp(contactId as Identifier, body)
      : sendSocial(threadId as Identifier, body);
  const sendFile = (materialId: Identifier) =>
    isWhatsapp
      ? sendWhatsappMaterial(contactId as Identifier, materialId)
      : sendSocialMaterial(threadId as Identifier, materialId);

  const sendWelcome = async () => {
    setSending(true);
    try {
      await sendWhatsappWelcome(contactId as Identifier);
      notify("Bienvenida enviada por WhatsApp", { type: "success" });
      reload();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        {
          type: "error",
        },
      );
    } finally {
      setSending(false);
    }
  };

  const sentMaterialIds = new Set(
    messages
      .filter((message) => message.material_id != null)
      .map((message) => String(message.material_id)),
  );

  const sendMaterial = async (material: Material) => {
    try {
      await sendFile(material.id);
      notify(`Enviado: ${material.title}`, { type: "success" });
      reload();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        { type: "error" },
      );
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await sendText(body);
      setText("");
      reload();
    } catch (error) {
      notify(
        error instanceof WindowClosedError
          ? isWhatsapp
            ? "Han pasado más de 24 h desde su último mensaje: hace falta un mensaje aprobado"
            : "Han pasado más de 24 h desde su último mensaje: podrás escribirle cuando vuelva a escribir"
          : error instanceof Error
            ? error.message
            : "No se ha podido enviar",
        { type: "error" },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-[#efeae2] dark:bg-neutral-900",
        className,
      )}
    >
      <div
        ref={listRef}
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-3"
      >
        {isPending ? (
          <p className="text-center text-sm text-neutral-500">Cargando…</p>
        ) : messages.length ? (
          messages.map((message) => (
            <Bubble
              key={message.id}
              message={message}
              senderName={
                message.sales_id
                  ? agentName(findAgent(message.sales_id)).split(" ")[0]
                  : undefined
              }
            />
          ))
        ) : (
          <p className="m-auto flex flex-col items-center gap-1 text-center text-sm text-neutral-500">
            <MessageCircle className="h-6 w-6" />
            Todavía no hay mensajes de {CHANNEL_LABEL[channel]} con este
            cliente.
          </p>
        )}
      </div>

      <div className="flex min-h-0 flex-col gap-2 border-t bg-background p-2">
        {windowOpen ? (
          <span className="shrink-0 px-1 text-[11px] text-muted-foreground">
            Puedes responder desde aquí · {windowLeft(lastInbound)}
          </span>
        ) : (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span>
              {!isWhatsapp
                ? `Han pasado más de 24 h desde su último mensaje. ${CHANNEL_LABEL[channel]} solo deja contestar durante 24 h: en cuanto vuelva a escribir, le respondes desde aquí.`
                : !welcomeReady
                  ? messages.length
                    ? "Han pasado más de 24 h desde su último mensaje. WhatsApp no deja escribirle libre hasta que vuelva a escribir: llámale y, en cuanto conteste por WhatsApp, le respondes desde aquí."
                    : "Este cliente aún no nos ha escrito por WhatsApp y WhatsApp no deja escribir primero sin un mensaje aprobado (la bienvenida está pendiente de que Meta la apruebe). Llámale; en cuanto nos escriba, le respondes desde aquí."
                  : messages.length
                    ? "Han pasado más de 24 h desde su último mensaje. WhatsApp exige retomar la conversación con un mensaje aprobado: en cuanto conteste, escribes libre desde aquí."
                    : "WhatsApp exige empezar con un mensaje aprobado. Mándale la bienvenida (su nombre y su franja de llamada) y en cuanto conteste escribes libre desde aquí."}
            </span>
            {isWhatsapp && phone && welcomeReady ? (
              <Button
                size="sm"
                className="h-7 rounded-full bg-lime-400 text-neutral-900 hover:bg-lime-300"
                disabled={sending}
                onClick={sendWelcome}
              >
                <Send className="h-3.5 w-3.5" />
                Enviar bienvenida
              </Button>
            ) : null}
          </div>
        )}

        {panel === "materials" ? (
          <MaterialPicker
            sentIds={sentMaterialIds}
            canSend={windowOpen}
            onSend={sendMaterial}
            onClose={() => setPanel(null)}
          />
        ) : null}

        {panel === "replies" ? (
          <div className="min-h-0 overflow-y-auto">
            <QuickRepliesPanel
              firstName={firstName}
              agentFirstName={myFirstName}
              onPick={(value) => {
                setText(value);
                setPanel(null);
              }}
            />
          </div>
        ) : null}

        <div className="flex shrink-0 items-end gap-2">
          <button
            type="button"
            onClick={() =>
              setPanel((value) => (value === "materials" ? null : "materials"))
            }
            className={cn(
              "rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground",
              panel === "materials" && "bg-muted text-foreground",
            )}
            title="Enviar material: vídeos, precios, ficha, fotos"
          >
            <FolderOpen className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() =>
              setPanel((value) => (value === "replies" ? null : "replies"))
            }
            className={cn(
              "rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground",
              panel === "replies" && "bg-muted text-foreground",
            )}
            title="Respuestas rápidas"
          >
            <Zap className="h-4 w-4" />
          </button>
          <Textarea
            value={text}
            rows={1}
            placeholder={
              windowOpen
                ? "Escribe un mensaje"
                : "Podrás escribir libre cuando conteste"
            }
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && windowOpen) {
                event.preventDefault();
                send();
              }
            }}
            className="max-h-32 min-h-[40px] resize-none rounded-2xl"
          />
          <Button
            size="icon"
            className="shrink-0 rounded-full bg-lime-400 text-neutral-900 hover:bg-lime-300"
            disabled={!windowOpen || sending || !text.trim()}
            onClick={send}
            title="Enviar"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
